import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Menu, 
  X, 
  Anchor, 
  Scale, 
  Phone, 
  ChevronDown,
  Truck,
  Clock,
  Tag,
  PackageX,
  Users,
  Shield,
  Target,
  Heart,
  Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCompare } from '@/context/CompareContext';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { compareList } = useCompare();

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white/95 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50" data-testid="navbar">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <div className="w-10 h-10 bg-slate-900 rounded-sm flex items-center justify-center group-hover:bg-orange-500 transition-colors">
              <Anchor className="w-6 h-6 text-white" />
            </div>
            <div className="hidden sm:block">
              <span className="text-xl font-bold text-slate-900">EngineShip</span>
              <span className="text-xl font-bold text-orange-500">Parts</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Home */}
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                isActive('/') ? 'text-orange-500 bg-orange-50' : 'text-slate-600 hover:text-orange-500 hover:bg-slate-50'
              }`}
            >
              Home
            </Link>

            {/* Inventory */}
            <Link
              to="/inventory"
              className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                isActive('/inventory') ? 'text-orange-500 bg-orange-50' : 'text-slate-600 hover:text-orange-500 hover:bg-slate-50'
              }`}
            >
              Inventory
            </Link>

            {/* Ready Now - RED */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50 rounded-sm transition-colors">
                <span className="relative flex h-2 w-2 mr-1">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600"></span>
                </span>
                Ready Now
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/inventory?is_ready_to_ship=true" className="flex items-center gap-2 text-red-600">
                    <Truck className="w-4 h-4" />
                    Ready to Ship
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/inventory?ready_7_days=true" className="flex items-center gap-2 text-red-600">
                    <Clock className="w-4 h-4" />
                    Ready within 7 Days
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/inventory?special_pricing=true" className="flex items-center gap-2 text-red-600">
                    <Tag className="w-4 h-4" />
                    Special Pricing
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/inventory?liquidation=true" className="flex items-center gap-2 text-red-600">
                    <PackageX className="w-4 h-4" />
                    Liquidation
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* About Us */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-slate-600 hover:text-orange-500 hover:bg-slate-50 rounded-sm transition-colors">
                About Us
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/about/processes" className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-orange-500" />
                    Our Processes & Guarantee
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/about/team" className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-orange-500" />
                    Sales Team
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/about/purpose" className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-orange-500" />
                    Our Purpose & Values
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Sell Your Equipment */}
            <Link
              to="/sell-equipment"
              className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                isActive('/sell-equipment') ? 'text-orange-500 bg-orange-50' : 'text-slate-600 hover:text-orange-500 hover:bg-slate-50'
              }`}
            >
              Sell Your Equipment
            </Link>

            {/* Contact */}
            <Link
              to="/contact"
              className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                isActive('/contact') ? 'text-orange-500 bg-orange-50' : 'text-slate-600 hover:text-orange-500 hover:bg-slate-50'
              }`}
            >
              Contact
            </Link>

            {/* Track Order */}
            <Link
              to="/track"
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                isActive('/track') ? 'text-orange-500 bg-orange-50' : 'text-slate-600 hover:text-orange-500 hover:bg-slate-50'
              }`}
              data-testid="track-order-nav-link"
            >
              <Truck className="w-4 h-4" />
              Track Order
            </Link>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-3">
            {/* Compare Button */}
            <Link to="/compare" data-testid="compare-link">
              <Button variant="ghost" className="relative">
                <Scale className="w-5 h-5" />
                {compareList.length > 0 && (
                  <Badge className="absolute -top-1 -right-1 w-5 h-5 p-0 flex items-center justify-center bg-orange-500">
                    {compareList.length}
                  </Badge>
                )}
              </Button>
            </Link>

            {/* Call CTA */}
            <a href="tel:+12012344851" className="hidden md:flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Phone className="w-4 h-4 text-orange-500" />
              +1 (201) 234-4851
            </a>

            {/* Contact CTA */}
            <Link to="/contact" className="hidden md:block">
              <Button className="btn-primary" data-testid="get-quote-btn">
                Get Quote
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden p-2 hover:bg-slate-100 rounded-sm"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden border-t border-slate-200 py-4 animate-fade-in" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-sm"
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/inventory"
                className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-sm"
                onClick={() => setIsOpen(false)}
              >
                Inventory
              </Link>
              
              {/* Ready Now Mobile */}
              <div className="px-4 py-2">
                <p className="text-sm font-bold text-red-600 mb-2 flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600"></span>
                  </span>
                  Ready Now
                </p>
                <div className="pl-4 space-y-1">
                  <Link to="/inventory?is_ready_to_ship=true" className="block py-2 text-sm text-red-600" onClick={() => setIsOpen(false)}>
                    Ready to Ship
                  </Link>
                  <Link to="/inventory?ready_7_days=true" className="block py-2 text-sm text-red-600" onClick={() => setIsOpen(false)}>
                    Ready within 7 Days
                  </Link>
                  <Link to="/inventory?special_pricing=true" className="block py-2 text-sm text-red-600" onClick={() => setIsOpen(false)}>
                    Special Pricing
                  </Link>
                  <Link to="/inventory?liquidation=true" className="block py-2 text-sm text-red-600" onClick={() => setIsOpen(false)}>
                    Liquidation
                  </Link>
                </div>
              </div>

              {/* About Us Mobile */}
              <div className="px-4 py-2">
                <p className="text-sm font-medium text-slate-900 mb-2">About Us</p>
                <div className="pl-4 space-y-1">
                  <Link to="/about/processes" className="block py-2 text-sm text-slate-600" onClick={() => setIsOpen(false)}>
                    Our Processes & Guarantee
                  </Link>
                  <Link to="/about/team" className="block py-2 text-sm text-slate-600" onClick={() => setIsOpen(false)}>
                    Sales Team
                  </Link>
                  <Link to="/about/purpose" className="block py-2 text-sm text-slate-600" onClick={() => setIsOpen(false)}>
                    Our Purpose & Values
                  </Link>
                </div>
              </div>

              <Link
                to="/sell-equipment"
                className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-sm"
                onClick={() => setIsOpen(false)}
              >
                Sell Your Equipment
              </Link>
              <Link
                to="/contact"
                className="px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-sm"
                onClick={() => setIsOpen(false)}
              >
                Contact
              </Link>
              <Link
                to="/track"
                className="flex items-center gap-2 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-sm"
                onClick={() => setIsOpen(false)}
                data-testid="track-order-mobile-link"
              >
                <Truck className="w-4 h-4" />
                Track Order
              </Link>
              
              <div className="px-4 pt-4">
                <Link to="/contact" onClick={() => setIsOpen(false)}>
                  <Button className="btn-primary w-full">Get Quote</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
